/**
 * Theme: system / light / dark. The canvas base is night in dark and white
 * in light; tools stay paper. Stored per device; applied as data-theme on
 * <html> so CSS tokens, the map background and the globe follow it.
 */
import { useSyncExternalStore } from 'react';

export type ThemeSetting = 'system' | 'light' | 'dark';
export type EffectiveTheme = 'light' | 'dark';

const KEY = 'drawearth.theme';
const listeners = new Set<() => void>();
let setting: ThemeSetting = 'system';

function systemTheme(): EffectiveTheme {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function getThemeSetting(): ThemeSetting {
  return setting;
}

export function effectiveTheme(): EffectiveTheme {
  return setting === 'system' ? systemTheme() : setting;
}

function apply(): void {
  const t = effectiveTheme();
  document.documentElement.dataset.theme = t;
  document.documentElement.style.colorScheme = t;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', t === 'light' ? '#ffffff' : '#0b0d14');
  for (const l of listeners) l();
}

export function setThemeSetting(next: ThemeSetting): void {
  setting = next;
  try {
    if (next === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, next);
  } catch {
    // storage unavailable
  }
  apply();
}

export function initTheme(): void {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark') setting = stored;
  } catch {
    // storage unavailable
  }
  apply();
  if (typeof matchMedia !== 'undefined') matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => apply());
}

export function onThemeChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useTheme(): { setting: ThemeSetting; theme: EffectiveTheme } {
  const snapshot = () => `${setting}:${effectiveTheme()}`;
  useSyncExternalStore(onThemeChange, snapshot, snapshot);
  return { setting, theme: effectiveTheme() };
}

/**
 * Colours the canvas surfaces use per theme (mirrors the CSS tokens). The
 * sphere takes the opposite of the space behind it: white on night, black
 * on white, so it always reads as the hero.
 */
export function themeColours(t: EffectiveTheme = effectiveTheme()) {
  return t === 'light'
    // sphereGridAlpha: [meridian/equator, minor] — the map's graticule carries
    // the globe's shape on white, where the atmosphere rim has nothing to glow against.
    ? { ground: '#ffffff', graticule: '#7aa7d9', globe: 0x12141f, globeEmissive: 0x0b0d14, globeSpecular: 0x4a5a80, globeGrid: 0x58b6ff, gridOpacity: 0.3, atmosphere: 0x58b6ff, halo: '#2d7fd6', pulse: 0xffc233, sphereGridAlpha: [0.9, 0.5] as const }
    : { ground: '#0b0d14', graticule: '#58b6ff', globe: 0xe9edf6, globeEmissive: 0x2a3350, globeSpecular: 0x1a2035, globeGrid: 0x3b5a8f, gridOpacity: 0.22, atmosphere: 0x58b6ff, halo: '#58b6ff', pulse: 0xe2a300, sphereGridAlpha: [0.55, 0.3] as const };
}
